import {
  Box,
  Flex,
  Icon,
  List,
  ListItem,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { NotificationList as NotificationListResponse, Order } from '@diamond/shared/types';
import { Divider } from '@diamond/shared/ui';
import {
  buyerCaptureException,
  LIST_FEATURE_FLAGS,
  trackEvent,
  useFeatureFlag,
} from '@diamond/shared/utils';
import { TRACKING_EVENTS } from '@diamond/sol-buyer/constant';
import {
  applyPaymentInstructionSentryContext,
  buildOrderDetailPaymentProofRoute,
  resolvePaymentDestination,
} from '@diamond/sol-buyer/utils';
import {
  getOrderDetailOptions,
  NOTIFICATION_QUERY_KEY,
  putReadNotification,
  useAuthStore,
  useShipToStore,
} from '@diamond/sol-buyer-context';
import { ChevronRightOutlined } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import idLocale from 'date-fns/locale/id';
import Link from 'next/link';
import { useRouter } from 'next/router';
import {
  Fragment,
  MouseEvent as ReactMouseEvent,
  ReactNode,
  useMemo,
  useState,
} from 'react';
import reactStringReplace from 'react-string-replace';

import {
  type NotificationTarget,
  parseNotificationTarget,
} from './parseNotificationMetadata';

type NotificationListProps = {
  data: NotificationListResponse['data'];
};

export function NotificationList({ data }: NotificationListProps) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const access_token = useAuthStore((s) => s.access_token);
  const selectedShipToId = useShipToStore((s) => s.selectedShipToId);
  const isPaymentInstructionEnabled = useFeatureFlag(
    LIST_FEATURE_FLAGS.FF_PAYMENT_INSTRUCTION_PAGE,
    false
  );
  const [resolvingNotificationId, setResolvingNotificationId] = useState<
    string | null
  >(null);

  const mutation = useMutation({
    mutationFn: (id: string) =>
      putReadNotification(access_token, id, {
        warehouse_id: selectedShipToId,
      }),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [NOTIFICATION_QUERY_KEY],
      });
    },
  });

  const formatCreatedAt = (createdAt: number) =>
    format(new Date(createdAt), 'eeee, d MMM yyyy', { locale: idLocale });

  const dateHeaders = useMemo(
    () =>
      data.reduce<Record<string, number>>((acc, curr, i) => {
        const dateVal = formatCreatedAt(curr.created_at);
        if (!(dateVal in acc)) {
          acc[dateVal] = i;
        }
        return acc;
      }, {}),
    [data]
  );

  const formatMatchesTextToBold = (
    text: string | Array<ReactNode>,
    regexes: Array<RegExp>
  ) => {
    return regexes.reduce((acc, curr) => {
      acc = reactStringReplace(acc, curr, (match, i) => {
        return (
          <strong key={match + i}>
            <em>{match}</em>
          </strong>
        );
      });
      return acc;
    }, text) as Array<ReactNode>;
  };

  const captureNotificationResolutionFailure = (
    target: NotificationTarget,
    reason: string,
    error?: unknown,
    paymentSubState?: Order['payment_sub_state']
  ) => {
    buyerCaptureException(
      {
        error: error instanceof Error ? error.message : String(error ?? ''),
        message: 'Failed to resolve waiting-payment notification',
        reason,
      },
      (scope) => {
        applyPaymentInstructionSentryContext(scope, {
          orderMetaId: target.kind === 'order' ? target.orderMetaId : undefined,
          paymentSubState,
          reason,
          source: 'notification',
          warehouse: target.kind === 'order' ? target.warehouse : undefined,
        });
      }
    );
  };

  const handleNotificationClick = async (
    event: ReactMouseEvent,
    id: string,
    isRead: boolean,
    target: NotificationTarget
  ) => {
    if (!isRead) mutation.mutate(id);

    if (target.kind === 'unknown') {
      captureNotificationResolutionFailure(
        target,
        'unknown-notification-metadata'
      );
      return;
    }

    const isPlainLeftClick =
      event.button === 0 &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey;

    if (
      !isPlainLeftClick ||
      !isPaymentInstructionEnabled ||
      !target.isWaitingForPayment ||
      target.kind !== 'order' ||
      resolvingNotificationId === id
    ) {
      return;
    }

    event.preventDefault();
    setResolvingNotificationId(id);

    try {
      const order = await queryClient.fetchQuery(
        getOrderDetailOptions(
          access_token,
          target.orderMetaId,
          target.warehouse
        )
      );
      const destination = resolvePaymentDestination({
        isPaymentInstructionEnabled,
        orderMetaId: target.orderMetaId,
        paymentSubState: order.payment_sub_state,
        warehouse: target.warehouse,
      });

      trackEvent(TRACKING_EVENTS.WFP_ROUTE_RESOLVED, {
        entry_source: 'notification',
        ff_payment_instruction_page: true,
        order_meta_id: target.orderMetaId,
        payment_sub_state: order.payment_sub_state,
        platform: 'web',
        presentation: 'page',
        resolved_destination: destination.kind,
        source: 'notification',
        sub_state: order.payment_sub_state,
        warehouse: target.warehouse,
      });
      await router.push(destination.href);
    } catch (error: unknown) {
      captureNotificationResolutionFailure(
        target,
        'notification-detail-resolution-failed',
        error
      );
      await router.push(
        buildOrderDetailPaymentProofRoute({
          orderMetaId: target.orderMetaId,
          warehouse: target.warehouse,
        })
      );
    } finally {
      setResolvingNotificationId(null);
    }
  };

  return (
    <List mt="24px">
      {data.map(({ id, title, is_read, body, metadata, created_at }, i) => {
        const target = parseNotificationTarget(metadata);
        const dateVal = formatCreatedAt(created_at);
        const isToday = dateVal === formatCreatedAt(Date.now());
        const isResolving = resolvingNotificationId === id;
        const notificationHref =
          !isPaymentInstructionEnabled &&
          target.kind === 'order' &&
          target.isWaitingForPayment
            ? buildOrderDetailPaymentProofRoute({
                orderMetaId: target.orderMetaId,
                warehouse: target.warehouse,
              })
            : target.href;

        return (
          <Fragment key={id}>
            {dateHeaders[dateVal] === i ? (
              <Box
                w="full"
                px="5"
                py="2"
                bg="#F2F2F7"
                rounded="lg"
                textAlign="center"
                color="#8E8E93"
                fontSize="sm"
                my="4"
              >
                {dateVal}
              </Box>
            ) : null}
            {i === 0 || dateHeaders[dateVal] === i ? null : <Divider />}
            <ListItem
              py="14px"
              display="flex"
              rounded="lg"
              as={Link}
              aria-busy={isResolving}
              onClick={(event) =>
                void handleNotificationClick(event, id, is_read, target)
              }
              href={notificationHref}
              _hover={{
                backgroundColor: 'hsl(199 100% 45% / 10%)',
              }}
            >
              <Box px="2">
                <Box
                  w="8px"
                  h="8px"
                  bg={is_read ? 'transparent' : 'blue'}
                  mt="2"
                  rounded="full"
                />
              </Box>
              <Box display="flex" flexDir="column" gap="0.5rem" flex="1">
                <Flex alignItems="center" justifyContent="space-between">
                  <Text
                    fontWeight={is_read ? 'normal' : 'bold'}
                    opacity={is_read ? 0.5 : 1}
                  >
                    {title}
                  </Text>
                  <Text fontSize="sm" color="#8E8E93" pr="5">
                    {isToday ? 'Hari ini, ' : ''}
                    {format(new Date(created_at), 'HH:mm', {
                      locale: idLocale,
                    })}
                  </Text>
                </Flex>
                <Flex alignItems="center" justifyContent="space-between">
                  <Text opacity={is_read ? 0.5 : 1}>
                    {formatMatchesTextToBold(body, [
                      /(SOL\d{9,}[0-9-]*)/g,
                      /(\d{2}\/\d{2}\/\d{4} • \d{2}:\d{2} [A-Z]{3,4})/g,
                    ])}
                  </Text>
                  <Box pr="5">
                    {isResolving ? (
                      <Spinner color="blue" size="sm" />
                    ) : (
                      <Icon color="#8E8E93" as={ChevronRightOutlined} />
                    )}
                  </Box>
                </Flex>
              </Box>
            </ListItem>
          </Fragment>
        );
      })}
    </List>
  );
}
